import { useEffect, useRef } from 'react';

export interface LiveMessage {
  type: string;
  data?: any;
  timestamp?: string;
}

/** Derive the WebSocket URL from the API base (…/api/v1 → /ws). */
const wsUrl = () => {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
  const u = new URL(base);
  return `${u.protocol === 'https:' ? 'wss' : 'ws'}://${u.host}/ws`;
};

/**
 * Live connection to the backend WebSocket service with automatic
 * reconnect + exponential backoff. Subscribes to the dashboard channel
 * and invokes `onMessage` for every server push.
 */
export function useLiveUpdates(
  onMessage: (m: LiveMessage) => void,
  channels: string[] = ['dashboard']
) {
  const handler = useRef(onMessage);
  handler.current = onMessage;

  useEffect(() => {
    let ws: WebSocket | null = null;
    let attempts = 0;
    let closed = false;
    let retryTimer: number | undefined;

    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(wsUrl());
      } catch {
        scheduleRetry();
        return;
      }
      ws.onopen = () => {
        attempts = 0;
        channels.forEach((channel) =>
          ws?.send(JSON.stringify({ type: 'subscribe', channel }))
        );
      };
      ws.onmessage = (event) => {
        try {
          handler.current(JSON.parse(event.data));
        } catch {
          /* ignore malformed frames */
        }
      };
      ws.onclose = scheduleRetry;
      ws.onerror = () => ws?.close();
    };

    const scheduleRetry = () => {
      if (closed) return;
      attempts += 1;
      const delay = Math.min(30000, 1000 * 2 ** attempts);
      retryTimer = window.setTimeout(connect, delay);
    };

    connect();
    return () => {
      closed = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      ws?.close();
    };
  }, []); // connect once; handler ref keeps the callback fresh
}
